// i18next-extract-mark-ns-start Step1
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import SlotsSummary from './SlotsSummary';
import OrderSummaryCloth from './OrderSummaryCloth';
import { tabletThresholdPixels, colors } from '../home/v4/styledComponents';
import { formatPrice } from '../../services/pricing';

const TotalRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-weight: 700;
  font-size: 18px;
  text-transform: uppercase;
  margin: 32px 0;
  padding: ${({ vertical }) => vertical ? '0 20px' : '0 17px'};
  ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom};`}
  ${({ grey }) => grey && `color: ${colors.grey200}; `}

  @media (max-width: ${tabletThresholdPixels}) {
    margin: 20px 0px;
    ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom};`}
    padding: 0 12px;
  }
`;
const LittleTotalRow = styled(TotalRow)`
  font-size: 16px;
  text-transform: uppercase;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 15px;
  }
`;
const SubTotalRow = styled(TotalRow)`
  font-size: 13px;
  font-weight: 400;
  margin-top: 0px;
  padding: 0 20px;
  text-transform: none;
  ${({ grey }) => grey && `color: ${colors.grey200}; margin-top: -15px;`}
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 12px;
    padding: 0 12px;
    ${({ grey }) => grey && `color: ${colors.grey200}; margin-top: -5px;`}
  }
`;
const Line = styled.div`
  border-width: 0.5px;
  border-style: solid;
  margin: ${(props) => props.marginTop}px 0px ${(props) => props.marginBottom}px;
  @media (max-width: ${tabletThresholdPixels}) {
    margin: 20px 0px 0px;
  }
`;
const AddressAndSlotsContainer = styled.div`
  margin-top: 32px;
  ${({ vertical }) => vertical && 'padding: 0 20px;'};
  @media (max-width: ${tabletThresholdPixels}) {
    margin-top: 20px;
    ${({ vertical }) => vertical && 'padding: 0 10px;'};
  }
`;
const PiecesContainer = styled.div`
  padding: ${({ vertical }) => vertical ? '0 20px' : '0 17px'};
  @media (max-width: ${tabletThresholdPixels}) {
    padding: 0 12px;
  }
`;
const Text = styled.p`
  font-size: 14px;
  margin: 5px 0 0;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 12px;
  }
`;
const CardsContainer = styled.div`
  margin-top: 25px;
  @media (max-width: ${tabletThresholdPixels}) {
    margin-top: 20px;
  }
`;
const SubtitleContainer = styled.div`
  background-color: ${({ ocher }) => ocher ? `${colors.beige700}` : `${colors.grey600}`};
  border-radius: 4px;
  padding: 10px;
  ${({ vertical }) => vertical && `background-color: ${colors.black}`};
`;
const Subtitle2 = styled.p`
  font-size:   ${({ vertical }) => vertical ? 16 : 22}px;
  text-transform: uppercase;
  color: ${colors.beige100};
  margin: 0;
`;

export default function OrderSummaryAtHome({
  vertical, selectedAddress, lastStep, isAddingNewAddress, selectedDates, clothesAtHome, hasDefinedPrice,
  totalClothes, hasUpcyclingWithoutPriceRange, promoCode, hasZipcode, deliveryFee, isDoubleProcess,
  rawClothes, deleteCloth, duplicateCloth, editingClothIndex, setEditingClothIndex, isAdviceOrder,
  pricingInverse, discounts, currency,
}) {
  const { t } = useTranslation();
  const renderSlots = () => (
    <SlotsSummary selectedDates={selectedDates} noMargin />
  );

  const renderAtHomeFee = () => (
    <>
      <LittleTotalRow>
        {t('Frais de déplacement')}
        {' '}
        <span>
          {promoCode.doesTilliOfferDeliveryFees ? t('Offerts') : `${formatPrice(deliveryFee.atHomeFee, currency)}`}
        </span>
      </LittleTotalRow>
      {!hasZipcode && !lastStep && (
        <SubTotalRow>
          {t('Les frais de déplacement vous seront précisés en fonction de votre code postal')}
        </SubTotalRow>
      )}
    </>
  );

  const renderAddress = () => (
    <Text>
      {`${selectedAddress.street} ${selectedAddress.zipcode}  ${selectedAddress.locality}`}
    </Text>
  );

  const renderSubTotal = () => {
    if (!hasDefinedPrice) {
      return (
        <LittleTotalRow>
          {t('Sous-Total')}
          {' '}
          <span>{t('Estimation en RDV')}</span>
        </LittleTotalRow>
      );
    }
    return (
      <LittleTotalRow>
        {t('Sous-Total')}
        {' '}
        <span>
          {formatPrice(totalClothes, currency)}
          {hasUpcyclingWithoutPriceRange ? t('+ estimation en RDV') : ''}
        </span>
      </LittleTotalRow>
    );
  };

  const renderPieces = () => clothesAtHome.map((cloth) => {
    const priceRange = cloth.upcyclingItems.reduce((acc, upcyclingItem) => acc + (upcyclingItem.priceRange || 0), 0);
    return (
      <OrderSummaryCloth
        key={`${cloth.index}_${cloth.name}_${cloth.slug}`}
        discounts={discounts}
        cloth={cloth}
        clothes={rawClothes}
        deleteCloth={deleteCloth}
        duplicateCloth={duplicateCloth}
        editingClothIndex={editingClothIndex}
        priceRange={priceRange}
        setEditingClothIndex={setEditingClothIndex}
        isAdviceOrder={isAdviceOrder}
        pricingInverse={pricingInverse}
        vertical={vertical}
        lastStep={lastStep}
        currency={currency}
      />
    );
  });

  return (
    <>
      <CardsContainer vertical={vertical}>
        <SubtitleContainer vertical={vertical}>
          <Subtitle2 vertical={vertical}>{t('À domicile')}</Subtitle2>
        </SubtitleContainer>
        {vertical
    && (
      <>
        <AddressAndSlotsContainer vertical={vertical}>
          {(selectedAddress && Object.keys(selectedAddress).length > 0 && !lastStep && !isAddingNewAddress) && renderAddress()}
          {(selectedDates && Object.keys(selectedDates).length > 0 && !lastStep) && renderSlots()}
        </AddressAndSlotsContainer>
        {((selectedDates && Object.keys(selectedDates).length > 0 && !lastStep)
        || (selectedAddress && Object.keys(selectedAddress).length > 0 && !lastStep && !isAddingNewAddress))
      && <Line marginTop={25} marginBottom={25} />}
      </>
    )}
        <PiecesContainer vertical={vertical}>
          {renderPieces()}
        </PiecesContainer>
      </CardsContainer>
      {(vertical && !isDoubleProcess) && renderSubTotal()}
      {vertical && renderAtHomeFee()}
    </>
  );
}

OrderSummaryAtHome.propTypes = {
  vertical: PropTypes.bool,
  selectedAddress: PropTypes.shape({
    street: PropTypes.string,
    zipcode: PropTypes.string,
    locality: PropTypes.string,
  }),
  lastStep: PropTypes.bool,
  isAddingNewAddress: PropTypes.bool,
  selectedDates: PropTypes.shape({}),
  clothesAtHome: PropTypes.arrayOf(PropTypes.shape({})),
  hasDefinedPrice: PropTypes.bool,
  totalClothes: PropTypes.number.isRequired,
  hasUpcyclingWithoutPriceRange: PropTypes.bool,
  promoCode: PropTypes.shape({
    doesTilliOfferDeliveryFees: PropTypes.bool,
  }),
  hasZipcode: PropTypes.bool,
  deliveryFee: PropTypes.shape({
    atHomeFee: PropTypes.number,
  }).isRequired,
  isDoubleProcess: PropTypes.bool,
  rawClothes: PropTypes.arrayOf(PropTypes.shape({})),
  deleteCloth: PropTypes.func.isRequired,
  duplicateCloth: PropTypes.func,
  editingClothIndex: PropTypes.number,
  setEditingClothIndex: PropTypes.func,
  isAdviceOrder: PropTypes.bool,
  pricingInverse: PropTypes.shape({}).isRequired,
  discounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currency: PropTypes.string,
};

OrderSummaryAtHome.defaultProps = {
  vertical: false,
  selectedAddress: null,
  lastStep: false,
  isAddingNewAddress: false,
  selectedDates: null,
  clothesAtHome: [],
  hasDefinedPrice: false,
  hasUpcyclingWithoutPriceRange: false,
  promoCode: null,
  hasZipcode: false,
  isDoubleProcess: false,
  rawClothes: [],
  duplicateCloth: () => {},
  setEditingClothIndex: () => {},
  isAdviceOrder: false,
  editingClothIndex: null,
  currency: 'EUR',
};
